import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useLocalStorage } from "react-use";

import { Specimen } from "pwapi";

import { CaseDiagnosis } from "custom-models";

import { showMask } from "shared/actions/slideDetailActions";
import { setInputMacroCode } from "shared/actions/specimenDetailActions";
import { LesionLine, lesionLineTypeByName } from "shared/reducers/aiReducer";
import { macroCodesWithSubtypePossibilities } from "shared/utils/aiLinesAndSubtypes";
import { caseSlideLabel } from "shared/utils/slideUtils";

import { selectSpecimenClassification } from "selectors/classificationSelector";
import { useAppSelector } from "store/hooks";

type DiagnosisDialogAIProps = {
  currentSpecimen?: Specimen;
  currentDiagnosis: CaseDiagnosis;
  prospectiveMacroCode: string;
  updateDiagnosis: Function;
  positiveLesionLines?: LesionLine[];
  lesionLine?: LesionLine;
  setProspectiveMacroCode?: React.Dispatch<React.SetStateAction<string>>;
};
const DiagnosisDialogAI = ({
  currentDiagnosis,
  updateDiagnosis,
  positiveLesionLines,
  currentSpecimen,
  prospectiveMacroCode,
  setProspectiveMacroCode,
}: DiagnosisDialogAIProps) => {
  const dispatch = useDispatch();

  const caseDetail = useAppSelector(({ caseDetail }) => caseDetail);

  const specimenClassification = useAppSelector((state) =>
    selectSpecimenClassification(state, currentSpecimen?.id)
  );

  const inputMacroCode = useAppSelector(
    (state) =>
      state.caseDetail.specimenDetail.find((s) => s.id === currentSpecimen?.id)
        ?.inputMacroCode
  );

  const positiveLines = [
    ...new Set(positiveLesionLines?.map((ll) => ll.diagnosis_name)),
  ].map((diagnosis_name) => {
    const classificationesWithLines =
      specimenClassification?.filter((s) =>
        s?.lesion_lines?.some(
          (ll) =>
            ll.diagnosis_name === diagnosis_name &&
            ll.diagnosis_result === "POSITIVE"
        )
      ) || [];

    const linesOfName = classificationesWithLines
      .flatMap((c) => c?.lesion_lines || [])
      .filter(
        (ll) =>
          ll.diagnosis_name === diagnosis_name &&
          ll.diagnosis_result === "POSITIVE"
      )
      .filter((v) => v);

    return {
      diagnosis_name,
      linesOfName,
      subtypes: [
        ...new Set(
          positiveLesionLines
            ?.filter((ll) => ll.diagnosis_name === diagnosis_name)
            .flatMap((ll) =>
              ll.subtypes?.filter((s) =>
                ["POSITIVE", "INCONCLUSIVE"].includes(s.diagnosis_result)
              )
            )
            // .sort((a, b) => a.severity - b.severity) TODO: sort subtype by severity? how does this work elsewhere?
            .map(
              (s) =>
                `${s?.diagnosis}${
                  s?.diagnosis_result === "INCONCLUSIVE"
                    ? " (Inconclusive)"
                    : ""
                }`
            )
        ),
      ],
      slide_ids: classificationesWithLines.map((s) => s?.slide_id),
    };
  });

  const allDxcodes = [
    ...new Set(
      positiveLines.flatMap((p) =>
        p.linesOfName.flatMap((l) => macroCodesWithSubtypePossibilities(l))
      )
    ),
  ].filter((v) => v);

  // Ported from DiagnosisDialog.tsx, probably better belongs here
  // Sets the input if only 1 macro code exists
  useEffect(() => {
    if (
      setProspectiveMacroCode &&
      allDxcodes.length === 1 &&
      currentSpecimen?.id &&
      !prospectiveMacroCode &&
      typeof inputMacroCode === "undefined"
    ) {
      setProspectiveMacroCode(allDxcodes.at(0) || "");
      dispatch(setInputMacroCode(currentSpecimen?.id, allDxcodes.at(0) || ""));
    }
  }, [allDxcodes, currentSpecimen?.id, prospectiveMacroCode, inputMacroCode]);

  // Hack to fix widths on the subtypes
  const [commonWidth] = useLocalStorage<number | undefined>(
    "DIAGNOSIS_TEXTAREA_WIDTH"
  );
  // If it's under 400, bump it to 400
  const subtypeMaxWidth = commonWidth
    ? commonWidth < 400
      ? 400
      : commonWidth
    : undefined;

  return (
    <div>
      {(!positiveLesionLines || positiveLesionLines?.length < 1) && (
        <div className="Sidebar__AI">
          <p>
            <strong>No Positive Lesion Lines</strong>
          </p>
        </div>
      )}

      {positiveLines.map((p) => {
        const dxcodes = [
          ...new Set(
            p.linesOfName.map((line) =>
              macroCodesWithSubtypePossibilities(line)
            )
          ),
        ];
        const slides = caseDetail.slides?.filter((s) =>
          p.slide_ids.includes(s.id)
        );
        const margins = [
          ...new Set(p.linesOfName.map((l) => l.margin_status)),
        ].filter((v) => v);

        return (
          <div className="Sidebar__AI">
            {false && (
              <button
                type="button"
                className={classNames("Sidebar__AI__ThumbsDown", {
                  thumbsDown: currentDiagnosis.is_ai_mismatch,
                })}
                onClick={() =>
                  updateDiagnosis({
                    is_ai_mismatch: !currentDiagnosis.is_ai_mismatch,
                  })
                }
              >
                <FontAwesomeIcon icon="thumbs-down" />
              </button>
            )}
            <div>
              <div className="Sidebar__AI__diagnosis">
                <p>
                  <span>
                    {p.diagnosis_name}
                    {!!slides?.length && (
                      <>
                        {" "}
                        &mdash;{" "}
                        {slides
                          .map((s) => caseSlideLabel(s, caseDetail))
                          .join(", ")}
                      </>
                    )}
                  </span>
                  <span
                    className="LesionLineType__subtypes"
                    style={{ maxWidth: subtypeMaxWidth }}
                  >
                    <span className="LesionLineType__legend">
                      {p.subtypes.join(", ")}
                      {/* TODO: separate conclusive, inconclusive */}
                    </span>
                    {!!margins.length && (
                      <>
                        {" "}
                        &mdash;{" "}
                        <code>
                          {margins.join(", ")} Margin
                          {margins.length > 1 && <>s</>}
                        </code>
                      </>
                    )}
                  </span>
                  {/* <SubtypesAndMargin lesionLine={diagnosableLesionLine} /> */}
                  {/* positive margins [only], positive & negative margins, negative margins [only] */}
                </p>
              </div>
              {dxcodes.map((dxcode) => {
                if (dxcode)
                  return (
                    <button
                      key={lesionLineTypeByName(dxcode)}
                      className={classNames("Sidebar__AI__macro", {
                        "Sidebar__AI__macro--selected":
                          dxcode === inputMacroCode,
                      })}
                      title={dxcode}
                      onClick={() => {
                        currentSpecimen?.id &&
                          dispatch(
                            setInputMacroCode(currentSpecimen?.id, dxcode)
                          );
                        dispatch(
                          showMask(
                            lesionLineTypeByName(p?.diagnosis_name || "")
                          )
                        );
                      }}
                      onFocus={
                        setProspectiveMacroCode
                          ? () => setProspectiveMacroCode(dxcode)
                          : undefined
                      }
                      onBlur={
                        setProspectiveMacroCode
                          ? () => setProspectiveMacroCode("")
                          : undefined
                      }
                    >
                      <img src="/pw-white.png" alt="" />
                      <span>{dxcode}</span>
                    </button>
                  );
                return null;
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DiagnosisDialogAI;
